import React, { CSSProperties, ReactNode } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

import { createShouldForwardProp } from '../../../helpers/shouldForwardProp';

// components
import AampeTooltip from '../AampeTooltip/AampeTooltip';

export interface ToggleButtonProps {
    title: React.ReactNode;
    value: string | number;
    color?: string;
    isSelected: string | number;
    isLeft?: boolean;
    isRight?: boolean;
    onChange: (value: any) => void;
    icon?: any;
    tooltip?: ReactNode;
    style?: CSSProperties;
    className?: string;
}

interface AampeToggleButtonProps {
    buttons: ToggleButtonProps[];
    className?: string;
    areButtonsCurved?: boolean;
    selectedColor?: string;
}

interface InputProps {
    isSelected: () => boolean;
    isLeft: boolean;
    isRight: boolean;
    color?: string;
    areButtonsCurved: boolean;
    selectedColor?: string;
    style?: CSSProperties;
    isMiddle?: boolean;
}

const shouldForwardProps = createShouldForwardProp([
    'isSelected',
    'isLeft',
    'isRight',
    'isMiddle',
    'selectedColor',
    'areButtonsCurved',
]);

const AampeToggleButton = ({
    buttons,
    className,
    areButtonsCurved = true,
    selectedColor = aampeTheme.colors.solidA01,
}: AampeToggleButtonProps) => {
    return (
        <Toggle className={className}>
            {buttons.map((item, i) => (
                <AampeTooltip key={item.value} title={item?.tooltip ?? ''}>
                    <InputButton
                        isLeft={item.isLeft || i === 0}
                        isRight={item.isRight || i === buttons.length - 1}
                        isSelected={() => item.isSelected === item.value}
                        onClick={() => item.onChange(item.value)}
                        color={item.color}
                        areButtonsCurved={!!areButtonsCurved}
                        selectedColor={selectedColor}
                        style={item.style}
                        className={item.className}
                        isMiddle={i > 0 && i < buttons.length - 1}
                    >
                        <Content $isSelected={item.isSelected === item.value}>
                            {item?.icon}
                            {item.title}
                        </Content>
                    </InputButton>
                </AampeTooltip>
            ))}
        </Toggle>
    );
};
export default AampeToggleButton;

const Toggle = styled.div(() => ({
    backgroundColor: aampeTheme.colors.white,
    borderRadius: aampeTheme.borderRadius.default,
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const InputButton = styled.button.withConfig({
    shouldForwardProp: shouldForwardProps,
})<InputProps>((props) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color:
        props.isSelected() && props.selectedColor
            ? props.selectedColor
            : props.color || aampeTheme.colors.textAccessibleWG01,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRight: props.isLeft ? 'none' : props.isMiddle ? 'none' : '',
    backgroundColor: props.isSelected()
        ? aampeTheme.colors.backgroundWG01
        : aampeTheme.colors.white,
    borderRadius: props.areButtonsCurved
        ? props.isLeft
            ? aampeTheme.spacing4(1, 0, 0, 1)
            : props.isRight
            ? aampeTheme.spacing4(0, 1, 1, 0)
            : ''
        : props.isLeft
        ? aampeTheme.spacing4(0.5, 0, 0, 0.5)
        : props.isRight
        ? aampeTheme.spacing4(0, 0.5, 0.5, 0)
        : '',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
        color: props.selectedColor
            ? props.selectedColor
            : aampeTheme.colors.interactiveWG01,
    },
    ...props.style,
    height: '36px',
    padding: aampeTheme.spacing4(0, 2),
}));

const Content = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ $isSelected: boolean }>(({ $isSelected }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: $isSelected
        ? aampeTheme.weights.medium
        : aampeTheme.weights.light,
    fontSize: aampeTheme.size.font.default,
    lineHeight: aampeTheme.size.lineHeight.default,
    gap: aampeTheme.spacing4(2),
    textWrap: 'nowrap',
    minWidth: '44px',
}));
